import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Check, ChevronDown, ChevronRight, X, ChevronLeft, ChevronRight as ChevronRightIcon, Images, Play } from "lucide-react";
import * as THREE from "three";
import { SERVICES, IMAGES } from "../data/site";
import logoWhite1 from "../data/images/logowhite1.png";
import { BeforeAfter } from "../components/BeforeAfter";
import { SectionHeading } from "../components/SectionHeading";
import { Seo } from "../components/Seo";
import { buildServicePageSEO, serviceSchema, faqSchema, breadcrumbSchema } from "../data/seo";

const getBannerObjectPosition = (focusValue) => {
  const raw = typeof focusValue === "string" ? focusValue.trim().toLowerCase() : "";
  if (!raw || raw === "center") return "50% 50%";
  if (raw === "top") return "50% 0%";
  if (raw === "bottom") return "50% 100%";

  const offsetMatch = raw.match(/^(top|bottom)[-:\s]?(\d{1,3})$/);
  if (offsetMatch) {
    const direction = offsetMatch[1];
    const amount = Math.max(0, Math.min(100, Number(offsetMatch[2])));
    const yPos = direction === "top" ? amount : 100 - amount;
    return `50% ${yPos}%`;
  }

  const pairPercentMatch = raw.match(/^(\d{1,3})%\s+(\d{1,3})%$/);
  if (pairPercentMatch) {
    const xPos = Math.max(0, Math.min(100, Number(pairPercentMatch[1])));
    const yPos = Math.max(0, Math.min(100, Number(pairPercentMatch[2])));
    return `${xPos}% ${yPos}%`;
  }

  const singlePercentMatch = raw.match(/^(\d{1,3})%$/);
  if (singlePercentMatch) {
    const yPos = Math.max(0, Math.min(100, Number(singlePercentMatch[1])));
    return `50% ${yPos}%`;
  }

  return "50% 50%";
};

const ServiceSummary3DAccent = () => {
  const mountRef = useRef(null);
  const [hideForFooter, setHideForFooter] = useState(false);

  useEffect(() => {
    const footer = document.querySelector('[data-testid="site-footer"]');
    if (!footer) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setHideForFooter(entry.isIntersecting);
      },
      { threshold: 0.01 }
    );

    observer.observe(footer);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!mountRef.current) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const mount = mountRef.current;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 100);
    camera.position.set(0, 0, 7.5);

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    mount.appendChild(renderer.domElement);

    const key = new THREE.DirectionalLight(0xffffff, 0.95);
    key.position.set(1.8, 2.2, 3.8);
    scene.add(key);
    scene.add(new THREE.AmbientLight(0xffffff, 0.42));

    const accentGroup = new THREE.Group();
    accentGroup.position.set(1.35, 0.18, -0.2);
    scene.add(accentGroup);

    const textureLoader = new THREE.TextureLoader();
    const logoTexture = textureLoader.load(logoWhite1);
    logoTexture.colorSpace = THREE.SRGBColorSpace;
    logoTexture.anisotropy = renderer.capabilities.getMaxAnisotropy();

    const logoGeometry = new THREE.PlaneGeometry(4, 4);
    const logoMaterial = new THREE.MeshBasicMaterial({
      map: logoTexture,
      transparent: true,
      opacity: 0.78,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
    });
    const logoMesh = new THREE.Mesh(logoGeometry, logoMaterial);
    logoMesh.rotation.y = -0.15;
    accentGroup.add(logoMesh);

    const haloGeometry = new THREE.RingGeometry(1.15, 1.24, 80);
    const haloMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.12,
      side: THREE.DoubleSide,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const haloMesh = new THREE.Mesh(haloGeometry, haloMaterial);
    haloMesh.position.z = -0.08;
    accentGroup.add(haloMesh);

    const particleCount = 90;
    const particlePositions = new Float32Array(particleCount * 3);
    for (let i = 0; i < particleCount; i += 1) {
      const i3 = i * 3;
      const radius = 1.25 + Math.random() * 1.4;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      particlePositions[i3] = radius * Math.sin(phi) * Math.cos(theta);
      particlePositions[i3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
      particlePositions[i3 + 2] = radius * Math.cos(phi);
    }
    const particleGeometry = new THREE.BufferGeometry();
    particleGeometry.setAttribute("position", new THREE.BufferAttribute(particlePositions, 3));
    const particleMaterial = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 0.012,
      transparent: true,
      opacity: 0.08,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const particles = new THREE.Points(particleGeometry, particleMaterial);
    accentGroup.add(particles);

    const pointer = { x: 0, y: 0 };
    const targetTilt = new THREE.Vector2(0, 0);
    const currentTilt = new THREE.Vector2(0, 0);
    const onPointerMove = (event) => {
      const bounds = mount.getBoundingClientRect();
      const nx = (event.clientX - bounds.left) / Math.max(bounds.width, 1);
      const ny = (event.clientY - bounds.top) / Math.max(bounds.height, 1);
      pointer.x = (nx - 0.5) * 2;
      pointer.y = (ny - 0.5) * 2;
      targetTilt.set(pointer.x * 0.08, pointer.y * 0.06);
    };
    mount.addEventListener("pointermove", onPointerMove);

    const setSize = () => {
      if (!mount) return;
      const width = mount.clientWidth || 1;
      const height = mount.clientHeight || 1;
      renderer.setSize(width, height);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    };
    setSize();

    const resizeObserver = new ResizeObserver(setSize);
    resizeObserver.observe(mount);

    const clock = new THREE.Clock();
    let rafId;
    const animate = () => {
      const t = clock.getElapsedTime();
      currentTilt.lerp(targetTilt, 0.05);

      logoMesh.rotation.y = -0.15 + Math.sin(t * 0.42) * 0.04 + currentTilt.x;
      logoMesh.rotation.x = Math.cos(t * 0.36) * 0.02 - currentTilt.y;
      logoMesh.position.y = Math.sin(t * 0.65) * 0.08;
      logoMesh.scale.setScalar(1 + Math.sin(t * 1.3) * 0.03);

      haloMesh.rotation.z = t * 0.2;
      haloMesh.scale.setScalar(1 + Math.cos(t * 0.8) * 0.03);
      particles.rotation.y -= 0.00018;
      accentGroup.rotation.y += 0.0002;
      accentGroup.position.y = 0.18 + Math.sin(t * 0.42) * 0.06;
      renderer.render(scene, camera);
      rafId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(rafId);
      mount.removeEventListener("pointermove", onPointerMove);
      resizeObserver.disconnect();
      logoGeometry.dispose();
      haloGeometry.dispose();
      particleGeometry.dispose();
      logoMaterial.dispose();
      haloMaterial.dispose();
      particleMaterial.dispose();
      logoTexture.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <div
      className={`pointer-events-none fixed inset-0 z-0 transition-opacity duration-500 ${hideForFooter ? "opacity-0" : "opacity-100"}`}
      aria-hidden
    >
      <div ref={mountRef} className="h-full w-full opacity-70" />
    </div>
  );
};

const AutoplayVideoCard = ({ src, title, serviceName, index }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!src) return;
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const video = videoRef.current;
        if (!video) return;
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.4 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [src]);

  if (!src) return null;

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full overflow-hidden group border border-ink/10 hover:border-gold transition-colors duration-300 bg-ink/5"
    >
      <video
        ref={videoRef}
        src={src}
        muted
        autoPlay
        loop
        playsInline
        preload="metadata"
        className="w-full h-full object-cover"
        aria-label={title || `${serviceName} video ${index + 1}`}
      />
      {title && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-ink/70 to-transparent px-2 py-2 pointer-events-none">
          <span className="text-white text-[10px]
           tracking-wide leading-tight">{title}</span>
        </div>
      )}
    </div>
  );
};

const GalleryPanel = ({ images, videos = [], serviceName, onOpen }) => {
  const tabs = [
    ...(images.length > 0 ? [{ id: "photos", label: "Photos", count: images.length }] : []),
    ...(videos.length > 0 ? [{ id: "videos", label: "Videos", count: videos.length }] : []),
  ];
  const [activeTab, setActiveTab] = useState(tabs[0]?.id ?? "photos");
  const [showAll, setShowAll] = useState(false);
  const visibleCount = 4;

  // Reset showAll when switching tabs
  const handleTab = (id) => { setActiveTab(id); setShowAll(false); };

  const activeImages = activeTab === "photos" ? images : [];
  const activeVideos = activeTab === "videos" ? videos : [];
  const visible = showAll ? activeImages : activeImages.slice(0, visibleCount);
  const remaining = activeImages.length - visibleCount;

  return (
    <div >
      {/* Tab bar */}
      <div className="flex items-center gap-px mb-3 border-b border-ink/10">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => handleTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-[11px] tracking-[0.28em] uppercase font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab.id
                ? "border-gold text-gold"
                : "border-transparent text-ink/50 hover:text-ink"
            }`}
          >
            {tab.id === "photos" ? (
              <Images size={12} strokeWidth={1.5} />
            ) : (
              <Play size={12} strokeWidth={1.5} />
            )}
            {tab.label}
            <span className={`text-[9px] ${activeTab === tab.id ? "text-gold/60" : "text-ink/30"}`}>({tab.count})</span>
          </button>
        ))}
        {/* View all link */}
        {activeTab === "photos" && activeImages.length > visibleCount && (
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="ml-auto text-[10px] tracking-[0.25em] uppercase text-gold hover:text-ink transition-colors border-b border-gold/40 pb-px"
          >
            {showAll ? "Show less" : `View all ${activeImages.length}`}
          </button>
        )}
      </div>

      {/* Photos grid */}
      {activeTab === "photos" && (
        <div className="grid grid-cols-2 gap-1.5 h-full max-h-[50vh]">
          {visible.map((img, i) => {
            const isLastVisible = !showAll && i === visibleCount - 1 && remaining > 0;
            return (
              <button
                key={i}
                onClick={() => onOpen(i)}
                className="relative w-full overflow-hidden group border border-ink/10 hover:border-gold transition-colors duration-300"
                aria-label={`View image ${i + 1}`}
              >
                <img
                  src={img}
                  alt={`${serviceName} ${i + 1}`}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                {!isLastVisible && (
                  <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/25 transition-colors duration-300 flex items-center justify-center">
                    <Images size={18} strokeWidth={1.2} className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                )}
                {isLastVisible && (
                  <div
                    className="absolute inset-0 bg-ink/60 flex flex-col items-center justify-center gap-1"
                    onClick={(e) => { e.stopPropagation(); setShowAll(true); }}
                  >
                    <span className="text-white text-xl font-light">+{remaining}</span>
                    <span className="text-white/70 text-[10px] tracking-[0.25em] uppercase">more</span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Videos grid */}
      {activeTab === "videos" && (
        <div className="grid grid-cols-1">
          {activeVideos.map((video, i) => {
            const src = typeof video === "string" ? video : (video.url || video.videos || "");
            const title = typeof video === "object" ? video.title : null;
            return (
              <AutoplayVideoCard key={i} src={src} title={title} serviceName={serviceName} index={i} />
            );
          })}
        </div>
      )}
    </div>
  );
};

export const ServiceDetailPage = () => {
  const { slug, subslug } = useParams();
  const parentService = SERVICES.find((s) => s.slug === slug);
  const [openFaq, setOpenFaq] = useState(0);
  const [activeGalleryIdx, setActiveGalleryIdx] = useState(null);
  const hasGalleryHistoryEntry = useRef(false);

  // Keep hooks in stable order on every render.
  const subService = parentService && subslug
    ? parentService.highlights?.find((h) => h.slug === subslug)
    : null;
  const service = subService ?? parentService;
  const serviceGallery = Array.isArray(service?.gallery)
    ? service.gallery.filter(Boolean)
    : Array.isArray(service?.galleryimages)
    ? service.galleryimages.filter(Boolean)
    : [];
  const serviceVideos = Array.isArray(service?.videos) ? service.videos.filter(Boolean) : [];
  const hasSummary = Array.isArray(service?.summary) ? service.summary.some(Boolean) : Boolean(service?.summary);
  const hasMedia = serviceGallery.length > 0 || serviceVideos.length > 0;

  const closeGallery = useCallback(() => {
    if (hasGalleryHistoryEntry.current) {
      hasGalleryHistoryEntry.current = false;
      window.history.back();
      return;
    }
    setActiveGalleryIdx(null);
  }, []);

  const openGallery = useCallback((idx) => {
    setActiveGalleryIdx(idx);
  }, []);

  const firstMedia = (value) => (Array.isArray(value) ? value.find(Boolean) : value);

  const serviceImages = Array.isArray(service?.image)
    ? service.image.filter(Boolean)
    : [service?.image].filter(Boolean);
  const parentServiceImages = Array.isArray(parentService?.image)
    ? parentService.image.filter(Boolean)
    : [parentService?.image].filter(Boolean);
  const primaryServiceImage =
    serviceImages[0] || parentServiceImages[0] || IMAGES.clinicInterior1;

  const bannerHeroImage =
    firstMedia(service?.banner) ||
    firstMedia(service?.bannerImage) ||
    firstMedia(service?.bannerimage) ||
    firstMedia(parentService?.banner) ||
    firstMedia(parentService?.bannerImage) ||
    firstMedia(parentService?.bannerimage);

  // Prefer banner image when configured; otherwise fall back to service/parent image.
  const activeHeroImage = bannerHeroImage || primaryServiceImage;

  const bannerFocusRaw =
    service?.bannerfocus ||
    service?.bannerFocus ||
    parentService?.bannerfocus ||
    parentService?.bannerFocus ||
    "center";
  const bannerObjectPosition = getBannerObjectPosition(bannerFocusRaw);

  useEffect(() => {
    if (!service?.slug) return;
    setActiveGalleryIdx(null);
  }, [service?.slug]);

  // Keyboard and history for gallery popup
  useEffect(() => {
    if (activeGalleryIdx === null) return;

    window.history.pushState({ ...(window.history.state || {}), serviceGalleryOpen: true }, "");
    hasGalleryHistoryEntry.current = true;

    const onPop = () => {
      if (!hasGalleryHistoryEntry.current) return;
      hasGalleryHistoryEntry.current = false;
      setActiveGalleryIdx(null);
    };
    const onKey = (e) => {
      if (e.key === "Escape") closeGallery();
      if (e.key === "ArrowLeft") setActiveGalleryIdx((i) => (i - 1 + serviceGallery.length) % serviceGallery.length);
      if (e.key === "ArrowRight") setActiveGalleryIdx((i) => (i + 1) % serviceGallery.length);
    };

    window.addEventListener("popstate", onPop);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("popstate", onPop);
      window.removeEventListener("keydown", onKey);
    };
  }, [activeGalleryIdx, closeGallery, serviceGallery.length]);

  useEffect(() => {
    document.body.style.overflow = activeGalleryIdx !== null ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [activeGalleryIdx]);

  if (!parentService) return <Navigate to="/services" replace />;
  if (subslug && !subService) return <Navigate to={`/services/${slug}`} replace />;

  const seoData = buildServicePageSEO(service);

  return (
    <div data-testid="service-detail-page" className="relative overflow-x-hidden">
      <ServiceSummary3DAccent />
      <div className="relative z-10">
      <Seo
        title={seoData.title}
        description={seoData.description}
        image={primaryServiceImage}
        type="article"
        schemas={[
          serviceSchema(service),
          faqSchema(service.faqs?.length ? service.faqs : null),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Services", path: "/services" },
            ...(subService
              ? [
                  { name: parentService.name, path: `/services/${parentService.slug}` },
                  { name: service.name, path: `/services/${parentService.slug}/${service.slug}` },
                ]
              : [{ name: service.name, path: `/services/${service.slug}` }]
            ),
          ]),
        ]}
      />
      {/* Hero */}
      {/* <section className="relative h-[40vh] overflow-hidden bg-bone">
        <img
          src={activeHeroImage}
          alt={service.name}
          className="absolute inset-0 w-full h-full object-contain object-center transition-opacity duration-[1500ms] ease-in-out"
        />
      </section> */}
<section className="relative min-h-[45vh] overflow-hidden bg-cream">
  <img
    src={activeHeroImage}
    alt={service.name}
    className={`absolute inset-0 w-full h-full object-cover `}
    style={{ objectPosition: bannerObjectPosition }}
    // className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-[1500ms] ease-in-out`}
    // className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1500ms] ease-in-out`}
  />
</section>


      <section className="pt-2 container-x">
        {/* <nav className="text-[11px] tracking-[0.3em] uppercase text-ink/50 mb-3 flex flex-wrap items-center gap-2">
          <Link to="/" className="hover:text-gold">Home</Link>
          <ChevronRight size={12} />
          <Link to="/services" className="hover:text-gold">Services</Link>
          <ChevronRight size={12} />
          {subService && (
            <>
              <Link to={`/services/${parentService.slug}`} className="hover:text-gold">{parentService.name}</Link>
              <ChevronRight size={12} />
            </>
          )}
          <span className="text-gold-dark">{service.name}</span>
        </nav> */}
        {(service.category ?? parentService.category) && (
          <div className="label-eyebrow text-gold-dark mb-2">{service.category ?? parentService.category}</div>
        )}
        <h1 className="heading-display text-3xl md:text-5xl lg:text-6xl text-ink max-w-5xl">
          {service.name}
        </h1>
        {/* {service.tagline && (
          <p className="font-serif italic text-base md:text-xl text-ink/70 mt-2 max-w-3xl">
            {service.tagline}
          </p>
        )} */}
      </section>

      {/* Summary + Gallery */}
      {(hasSummary || hasMedia) && (
      <section className="py-8 container-x">
        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">

          {/* Left — Summary */}
          {hasSummary && (
          <div className={`${hasMedia ? "lg:col-span-6" : "lg:col-span-12"} text-lg leading-[1.8] text-ink/80`}>
            {Array.isArray(service.summary)
              ? service.summary.filter(Boolean).map((para, i) => (
                  <p key={i} className={`whitespace-pre-line${i > 0 ? " mt-6" : ""}`}>{para}</p>
                ))
              : <p className="whitespace-pre-line">{service.summary}</p>}
          </div>
          )}

          {/* Right — Tabbed Gallery */}
          {hasMedia && (
          <div className="lg:col-span-6  overflow-y-auto">
            <GalleryPanel
              images={serviceGallery}
              videos={serviceVideos}
              serviceName={service.name}
              onOpen={openGallery}
            />
          </div>
          )}

        </div>
      </section>
      )}

      {/* Service Highlights */}
      {service.highlights?.length > 0 && (
      <section className="py-10 md:py-3 container-x" data-testid="service-highlights-section">
        {/* <SectionHeading eyebrow="Included" title="What's" italicWord="involved." /> */}
        <div className={`mt-16 grid grid-cols-1 gap-px bg-ink/10 ${
          service.highlights.length === 1 ? "md:grid-cols-1" : 
          service.highlights.length === 2 ? "md:grid-cols-2" :
          service.highlights.length === 4 ? "md:grid-cols-2" : 
          "md:grid-cols-3"
        }`}>
          {service.highlights.map((s, i) => (
            <div
              key={s.slug}
              data-testid={`service-highlight-${s.slug}`}
              className="group bg-cream p-8 md:p-10 reveal flex flex-col"
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              {s.image && (
                <div className="aspect-[4/3] max-h-[180px] overflow-hidden mb-6">
                  <img src={s.image} alt={s.name} className="w-full h-full object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-110" />
                </div>
              )}
              <div className="label-eyebrow mb-3">0{i + 1}</div>
              <h3 className="font-serif text-2xl font-light leading-tight">{s.name}</h3>
              {s.tagline && <p className="font-serif italic text-gold-dark mt-2 text-sm">{s.tagline}</p>}
              {/* {s.summary && <p className="text-ink/60 text-sm mt-4 leading-relaxed flex-1">{s.summary}</p>} */}
              
              {/* <ul className="mt-6 flex flex-col gap-1.5">
                {s.benefits?.length > 0 && s.benefits.map((b) => (
                  <li key={b} className="flex items-center gap-2 text-[11px] tracking-wide text-ink/70">
                    <span className="w-1 h-1 rounded-full bg-gold flex-shrink-0" />
                    {b}
                  </li>
                ))}
              </ul> */}
              <Link
                to={`/services/${service.slug}/${s.slug}`}
                className="mt-6 text-[11px] tracking-[0.2em] uppercase text-gold hover:text-ink transition-colors flex items-center gap-1.5"
              >
                Read More <ChevronRight size={12} />
              </Link>
            </div>
          ))}
        </div>
      </section>
      )}

      {/* Benefits */}
      {service.benefits?.length > 0 && (
      <section className="py-24 md:py-32 bg-ink text-bone">
        <div className="container-x">
          <SectionHeading eyebrow="Benefits" title="Why patients" italicWord="choose us." light />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10">
            {service.benefits.map((b, i) => (
              <div key={i} className="bg-ink p-10 flex items-start gap-5 reveal" style={{ transitionDelay: `${i * 60}ms` }}>
                <Check size={20} strokeWidth={1.2} className="text-gold mt-1 flex-shrink-0" />
                <div className="font-serif text-2xl font-light leading-tight">{b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Process */}
      {service.process?.length > 0 && (
      <section className="py-24 md:py-32 container-x">
        <SectionHeading eyebrow="Process" title="How it" italicWord="unfolds." />
        <div className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-10">
          {service.process.map((p, i) => (
            <div key={i} className="reveal" style={{ transitionDelay: `${i * 80}ms` }} data-testid={`process-step-${i}`}>
              <div className="font-serif text-5xl md:text-6xl font-light text-gold mb-6">{String(i + 1).padStart(2, "0")}</div>
              {p.step && <h3 className="font-serif text-2xl font-medium mb-3">{p.step}</h3>}
              {p.body && <p className="text-ink/60 leading-relaxed">{p.body}</p>}
            </div>
          ))}
        </div>
      </section>
      )}


      {/* FAQ */}
      {service.faqs?.length > 0 && (
      <section className="py-24 md:py-32 container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5 reveal">
            <SectionHeading eyebrow="FAQ" title="Questions, " italicWord="answered." />
          </div>
          <div className="lg:col-span-7 reveal">
            <div className="divide-y divide-ink/15">
              {service.faqs.map((f, i) => (
                <div key={i} data-testid={`faq-item-${i}`}>
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                    className="w-full py-6 flex items-center justify-between text-left group"
                  >
                    <span className="font-serif text-xl md:text-2xl font-light pr-8 group-hover:text-gold transition-colors">{f.q}</span>
                    <ChevronDown size={20} strokeWidth={1.2} className={`flex-shrink-0 transition-transform duration-500 ${openFaq === i ? "rotate-180 text-gold" : ""}`} />
                  </button>
                  <div className={`overflow-hidden transition-all duration-500 ${openFaq === i ? "max-h-64 pb-8" : "max-h-0"}`}>
                    <p className="text-ink/70 leading-[1.8] pr-12">{f.a}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      )}

      {/* CTA Banner */}
      <section className="relative py-32 overflow-hidden bg-ink">
        {/* <div className="absolute inset-0">
          <img src={primaryServiceImage} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-ink/75" />
        </div> */}
        <div className="relative container-x text-center text-white">
          <h2 className="heading-display text-4xl md:text-6xl">
            Ready for{service.name && <span className="italic text-gold-light"> {service.name.toLowerCase()}?</span>}
          </h2>
          <Link to="/contact" data-testid="service-detail-cta" className="btn-outline-light mt-10 inline-flex">Book a Consultation</Link>
        </div>
      </section>

      {/* Service Gallery Lightbox */}
      {activeGalleryIdx !== null && serviceGallery.length > 0 && (
        <div
          className="fixed inset-0 z-[120] bg-[#808080] flex flex-col"
          onClick={closeGallery}
        >
          {/* Top bar */}
          <div
            className="grid grid-cols-[1fr_auto_1fr] items-center px-6 py-4 flex-shrink-0 gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <div className="text-xs tracking-[0.3em] uppercase text-white font-mono">
                {activeGalleryIdx + 1} / {serviceGallery.length}
              </div>
              <div className="text-sm text-white mt-0.5">{service.name}</div>
            </div>
            <div />
            <div className="flex items-center justify-end">
              <button
                onClick={closeGallery}
                className="w-9 h-9 border border-white/35 bg-white/20 flex items-center justify-center text-white hover:border-gold hover:text-gold transition-colors"
                aria-label="Close"
              >
                <X size={16} strokeWidth={1.4} />
              </button>
            </div>
          </div>

          {/* Image area */}
          <div
            className="flex-1 overflow-hidden flex items-center justify-center relative"
            onClick={(e) => e.stopPropagation()}
          >
            {serviceGallery.length > 1 && (
              <button
                onClick={() => setActiveGalleryIdx((i) => (i - 1 + serviceGallery.length) % serviceGallery.length)}
                className="fixed left-4 top-1/2 -translate-y-1/2 z-[130] w-11 h-11 border border-white/35 flex items-center justify-center text-white hover:border-gold hover:text-gold transition-colors bg-black/35"
                aria-label="Previous"
              >
                <ChevronLeft size={20} strokeWidth={1.4} />
              </button>
            )}
            <div className="w-full h-full flex items-center justify-center px-16 py-6">
              <img
                src={serviceGallery[activeGalleryIdx]}
                alt={`${service.name} gallery ${activeGalleryIdx + 1}`}
                className="max-h-[72vh] w-auto max-w-full object-contain"
                draggable={false}
              />
            </div>
            {serviceGallery.length > 1 && (
              <button
                onClick={() => setActiveGalleryIdx((i) => (i + 1) % serviceGallery.length)}
                className="fixed right-4 top-1/2 -translate-y-1/2 z-[130] w-11 h-11 border border-white/35 flex items-center justify-center text-white hover:border-gold hover:text-gold transition-colors bg-black/35"
                aria-label="Next"
              >
                <ChevronRightIcon size={20} strokeWidth={1.4} />
              </button>
            )}
          </div>

          {/* Bottom thumbnails */}
          {serviceGallery.length > 1 && (
            <div
              className="flex-shrink-0 px-4 md:px-6 py-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-2 overflow-x-auto pb-1 justify-start md:justify-center">
                {serviceGallery.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveGalleryIdx(i)}
                    aria-label={`View image ${i + 1}`}
                    className={`relative w-14 h-14 md:w-16 md:h-16 shrink-0 overflow-hidden border transition-all duration-300 ${
                      i === activeGalleryIdx
                        ? "border-gold ring-1 ring-gold/70"
                        : "border-white/35 hover:border-white/70"
                    }`}
                  >
                    <img src={img} alt={`thumbnail ${i + 1}`} className="w-full h-full object-cover" draggable={false} />
                    {i !== activeGalleryIdx && <div className="absolute inset-0 bg-black/30" />}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
};